/**
 * L2: Home tab view-model (01-references §6 S2): Favorites, Recently used and Pinned fields, with
 * 1Password's "Customize" (show, hide and reorder sections). Home preferences are UI state kept
 * in memory. They are never written to vault data (the data seam is read-only).
 */
import {
  splitFieldRef,
  type FieldRef,
  type HomePrefs,
  type HomeSectionId,
  type ItemField,
  type VaultItem,
  type VaultSnapshot,
  type WalletId,
} from "../contract.js";
import { isFavorite } from "./items.js";

export const HOME_SECTION_TITLE: Record<HomeSectionId, string> = {
  favorites: "Favorites",
  recent: "Recently Used",
  pinned: "Pinned Fields",
};

export const DEFAULT_HOME_PREFS: HomePrefs = {
  sections: [
    { id: "favorites", visible: true },
    { id: "recent", visible: true },
    { id: "pinned", visible: true },
  ],
};

const RECENT_LIMIT = 6;

export interface Pin {
  ref: FieldRef;
  item: VaultItem;
  field: ItemField;
}

export interface HomeSection {
  id: HomeSectionId;
  title: string;
  items?: VaultItem[];
  pins?: Pin[];
}

export interface HomeModel {
  sections: HomeSection[];
}

const live = (i: VaultItem) => !i.archived && !i.deleted;

export function resolvePins(snapshot: VaultSnapshot, pinned: FieldRef[]): Pin[] {
  const byId = new Map(snapshot.items.map((i) => [i.id, i]));
  const out: Pin[] = [];
  for (const ref of pinned) {
    const { itemId, fieldId } = splitFieldRef(ref);
    const item = byId.get(itemId);
    const field = item?.fields.find((f) => f.id === fieldId);
    if (item && field && live(item)) out.push({ ref, item, field });
  }
  return out;
}

export function homeModel(
  snapshot: VaultSnapshot,
  prefs: HomePrefs,
  pinned: FieldRef[],
  favoriteOverrides: Record<string, boolean>,
  scope: WalletId | null,
): HomeModel {
  const inScope = snapshot.items.filter((i) => live(i) && (scope === null || i.wallet === scope));
  const sections: HomeSection[] = [];
  for (const s of prefs.sections) {
    if (!s.visible) continue;
    if (s.id === "favorites") {
      const items = inScope.filter((i) => isFavorite(i, favoriteOverrides)).sort((a, b) => a.title.localeCompare(b.title));
      sections.push({ id: s.id, title: HOME_SECTION_TITLE[s.id], items });
    } else if (s.id === "recent") {
      const items = inScope
        .filter((i) => i.lastUsedAt)
        .sort((a, b) => (a.lastUsedAt! < b.lastUsedAt! ? 1 : a.lastUsedAt! > b.lastUsedAt! ? -1 : 0))
        .slice(0, RECENT_LIMIT);
      sections.push({ id: s.id, title: HOME_SECTION_TITLE[s.id], items });
    } else {
      const pins = resolvePins(snapshot, pinned).filter((p) => scope === null || p.item.wallet === scope);
      sections.push({ id: s.id, title: HOME_SECTION_TITLE[s.id], pins });
    }
  }
  return { sections };
}

export function toggleSection(prefs: HomePrefs, id: HomeSectionId): HomePrefs {
  return { sections: prefs.sections.map((s) => (s.id === id ? { ...s, visible: !s.visible } : { ...s })) };
}

/** Move a section up (-1) or down (+1). Bounded at the ends. */
export function moveSection(prefs: HomePrefs, id: HomeSectionId, delta: -1 | 1): HomePrefs {
  const sections = prefs.sections.map((s) => ({ ...s }));
  const i = sections.findIndex((s) => s.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= sections.length) return { sections };
  [sections[i], sections[j]] = [sections[j], sections[i]];
  return { sections };
}
